import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './instanceAgBadge.scss';

import ClusterApi from '../../api/ClusterApi';
import { ALWAYS_ON_SUPPORTED_TYPES } from '../Constants';
import { buildRedirectQuery } from '../../helpers/timeRangeUtils';
import { TimeRangeContext } from '../../context/TimeRangeContext';
import { useInstanceTargets } from '../../context/InstanceContext';

import InstanceAvailabilityGroup from '../../types/cluster/InstanceAvailabilityGroup';

// Role is not health, so it does not share HealthBadge's colour map: a primary is
// the normal, good state and would come out red there.
const ROLE_CLASS: Record<string, string> = {
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    RESOLVING: 'resolving',
};

// On a dashboard tile there is room for a letter, not a word. The colour and the
// tooltip carry the rest.
const ROLE_SHORT: Record<string, string> = {
    PRIMARY: 'P',
    SECONDARY: 'R',
};

const roleDescription = (role: string): string => {
    switch (role) {
        case 'PRIMARY':
            return 'this instance holds the primary replica';
        case 'SECONDARY':
            return 'this instance holds a secondary replica';
        case 'RESOLVING':
            return 'this replica is resolving its role';
        default:
            return 'the role of this replica is unknown';
    }
};

const RoleSegment = (props: { role: string, short?: boolean }) => (
    <span className={`ag-badge-role ${ROLE_CLASS[props.role] ?? 'unknown'}`}>
        {props.short ? (ROLE_SHORT[props.role] ?? '?') : props.role}
    </span>
);

// Shows the Always On availability groups a SQL Server instance replicates, each
// linking through to its group screen under Clusters. Renders nothing at all for
// instances that are not part of a group, which is the usual case. `compact` is
// for the dashboard tiles, where the role shrinks to a single letter.
function InstanceAgBadge(props: { instanceId: number, compact?: boolean }) {
    const { instanceId, compact } = props;
    const { timeRange } = useContext(TimeRangeContext);
    const { instances } = useInstanceTargets();
    const [groups, setGroups] = useState<InstanceAvailabilityGroup[]>([]);
    // Read from context rather than a prop so a late-arriving instance list still
    // resolves the type and triggers the lookup.
    const instanceType = instances.find(instance => instance.id === instanceId)?.type;

    useEffect(() => {
        setGroups([]);
        // Membership is only ever populated for the types that support Always On,
        // so anything else is spared the topology request.
        if (!(instanceId > 0) || !instanceType || !ALWAYS_ON_SUPPORTED_TYPES.includes(instanceType)) {
            return;
        }
        let active = true;
        ClusterApi.getInstanceMembership(instanceId).then(membership => {
            if (active) {
                setGroups(membership);
            }
        });
        return () => {
            active = false;
        };
    }, [instanceId, instanceType]);

    if (groups.length === 0) {
        return null;
    }

    // The context default carries no time range, so the link drops the period
    // rather than formatting an undefined date.
    const search = timeRange?.from ? `?${buildRedirectQuery(timeRange)}` : '';
    const link = (group: InstanceAvailabilityGroup) =>
        `/clusters/${group.clusterid}/availability-groups/${group.groupid}${search}`;

    // One group is the ordinary case and reads as a single chip. Roles differ from
    // group to group, so several cannot be said in one chip and become a menu.
    const className = `ag-badge${compact ? ' compact' : ''}`;

    if (groups.length === 1) {
        const [group] = groups;
        return (
            <Link className={className} to={link(group)}
                  data-tooltip-id="global-tooltip"
                  data-tooltip-content={`Always On availability group ${group.groupname} - ${roleDescription(group.role)}`}>
                <RoleSegment role={group.role} short={compact}/>
                <span className="ag-badge-name">
                    <span className="ag-badge-text">{group.groupname}</span>
                </span>
            </Link>
        );
    }

    return (
        <span className="dropdown d-inline">
            <button type="button" className={className} data-bs-toggle="dropdown" aria-expanded="false"
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="Always On availability groups this instance replicates">
                <span className="ag-badge-role unknown">AG</span>
                <span className="ag-badge-name">
                    <span className="ag-badge-text">{compact ? groups.length : `${groups.length} groups`}</span>
                    <i className="fal fa-angle-down" aria-hidden="true"/>
                </span>
            </button>
            <div className="dropdown-menu ag-badge-menu">
                {groups.map(group => (
                    <Link className="dropdown-item" key={group.groupid} to={link(group)}>
                        <RoleSegment role={group.role}/>
                        {group.groupname}
                    </Link>
                ))}
            </div>
        </span>
    );
}

export default InstanceAgBadge;
